import { SegmentedControl } from "@mantine/core";
import { useState } from "react";

import { useGetCatalog } from "../../../api/generated/endpoints/generation/generation";
import type { Voice } from "../../../api/generated/model";
import { CreateVoice } from "../../../platform/voices/CreateVoice";
import { NO_FILTER, VoiceFilters, type VoiceFilter } from "../../../platform/voices/VoiceFilters";
import { VoiceList } from "../../../platform/voices/VoiceList";
import classes from "./tabs.module.css";

/** Live's voice sources, in its order and words (`voice-select/constants.ts`), but "Recommended
 *  Voice", which only an actor's form has. */
const SOURCES = [
  { value: "global", label: "Global Voices" },
  { value: "team", label: "Custom Voices" },
  { value: "create", label: "Create Voice" },
] as const;
type Source = (typeof SOURCES)[number]["value"];

export interface VoicesTabProps {
  teamId: string;
  brandId: string;
}

/**
 * Pro's Library > Voices (spec ui-perf/pro-library-tabs): the voice picker's own lists as a page,
 * live's global voices with its filter, the team's own and Create Voice (record, upload or
 * design); a voice made opens Custom Voices with it marked. Each row plays its sample; a click
 * marks it, as the picker's does. Live has the picker alone, in the forms that take a voice.
 */
export function VoicesTab({ teamId, brandId }: VoicesTabProps) {
  const [source, setSource] = useState<Source>("global");
  const [filter, setFilter] = useState<VoiceFilter>(NO_FILTER);
  const [marked, setMarked] = useState<string | null>(null);
  const designModel = useGetCatalog().data?.audio_models.find((each) =>
    each.job_types.includes("voice.design"),
  );
  const mark = (voice: Voice) => setMarked(voice.id);
  return (
    <div className={classes.voices}>
      <div className={classes.row}>
        <SegmentedControl
          aria-label="Voices"
          data={[...SOURCES]}
          value={source}
          onChange={(next) => setSource(next as Source)}
        />
        {source === "global" ? <VoiceFilters applied={filter} onApply={setFilter} /> : null}
      </div>
      {source === "global" ? (
        <VoiceList
          teamId={teamId}
          brandId={brandId}
          params={{
            kind: "global",
            ...(filter.gender === null ? {} : { gender: filter.gender }),
            ...(filter.age === null ? {} : { age: filter.age }),
          }}
          chosen={marked}
          onChoose={mark}
          empty="No global voices found"
        />
      ) : source === "team" ? (
        <VoiceList
          teamId={teamId}
          brandId={brandId}
          params={{ kind: "team" }}
          chosen={marked}
          onChoose={mark}
          empty="No custom voices found"
        />
      ) : (
        <CreateVoice
          teamId={teamId}
          brandId={brandId}
          designModel={designModel}
          onCreated={(voice) => {
            setMarked(voice.id);
            setSource("team");
          }}
        />
      )}
    </div>
  );
}
